import { useAuth } from "Infrastructure/Contexts/AuthContext";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { ThemeProvider } from "@emotion/react";
import { Icon, useTheme } from "@mui/material";
import type { AuthProvider, AuthResponse } from "@toolpad/core";
import { AppProvider } from "@toolpad/core/AppProvider";
import { SignInPage } from "@toolpad/core/SignInPage";
import type { ReactNode } from "react";
import blogicLogo from "../../../Assets/Images/blogicLogo.svg";

type ToolpadSignInPageProps = {
  children?: ReactNode;
};

export const ToolpadSignInPage = ({ children }: ToolpadSignInPageProps) => {
  const { login } = useAuth();
  const { t } = useResource();
  const theme = useTheme();
  const { width, typography, customFontWeight, customShadows } = useTheme();

  const handleSignIn = async (
    provider: AuthProvider,
  ): Promise<AuthResponse> => {
    if (provider?.id === "google") {
      await login();
      return { type: "CredentialsSignin" };
    }
    return { type: "CredentialsSignin", error: "Unsupported provider" };
  };

  const providers = [
    {
      id: "google",
      name: "Google",
    },
  ];

  const branding = {
    logo: (
      <Icon sx={{ width: "auto", height: "auto" }}>
        <img src={blogicLogo} alt="BLogic Logo" style={{ height: 35 }} />
      </Icon>
    ),
    title: t(Resources.Common.AppTitle),
  };

  return (
    <ThemeProvider theme={theme}>
      <AppProvider
        theme={theme}
        branding={branding}
        localeText={{
          signInTitle: t(Resources.Auth.SignInTitle),
          signInSubtitle: t(Resources.Auth.SignInSubtitle),
          providerSignInTitle: (provider: string) => {
            if (provider.toLowerCase() === "google") {
              return t(Resources.Auth.SignInWithGoogle);
            }
            return provider;
          },
        }}
      >
        <SignInPage
          providers={providers}
          signIn={handleSignIn}
          sx={{
            "& .MuiContainer-root": {
              padding: 0,
              boxShadow: `${customShadows.lg} !important`,
              maxWidth: `${width.sm} !important`,
            },
            "& .MuiStack-root": {
              width: "100%",
              alignItems: "center",
            },
            "& h1": {
              fontSize: typography.h4.fontSize,
            },
            "& p": {
              fontSize: typography.body2.fontSize,
              fontWeight: customFontWeight.semiBold,
            },
          }}
          slotProps={{
            emailField: { sx: { display: "none" } },
            passwordField: { sx: { display: "none" } },
            submitButton: { sx: { display: "none" } },
            forgotPasswordLink: { sx: { display: "none" } },
            oAuthButton: {
              sx: {
                width: "auto",
                textTransform: "none !important",
                alignSelf: "center",
              },
            },
          }}
        />
        {children}
      </AppProvider>
    </ThemeProvider>
  );
};
